/**
 * Platform-correct display for a keyboard shortcut (brief §22 desktop pass
 * 2). The handlers that actually MATCH these keys (`ActiveVocabCard`'s
 * `handleCardKeyDown`) read `e.metaKey || e.ctrlKey`, `e.altKey` and
 * `e.shiftKey` — booleans the browser already sets from whichever physical
 * key the platform has (⌘/⌥ on a Mac keyboard, Ctrl/Alt on a Windows one), so
 * the BEHAVIOUR needs no platform branch. Only the LABEL does: a reader on
 * Windows has no ⌘ or ⌥ key printed anywhere on their keyboard, so a hint
 * spelled in Mac glyphs names keys they do not have.
 */

/** `navigator.platform` is deprecated but still the simplest reliable signal
 * for "which glyph set" — the same one-liner already used in
 * `QuickLookupPalette.tsx` and `TextImportPage.tsx` for the identical
 * ⌘V/Ctrl+V question. Read once at module load: the platform does not change
 * mid-session. */
export const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)

/** One physical key or modifier, platform-neutral. `'mod'` is the
 * Cmd-on-Mac/Ctrl-elsewhere modifier every other shortcut in this app already
 * uses that name for. */
export type KeyToken = 'mod' | 'Shift' | 'Alt' | 'Tab' | 'Enter' | 'Escape' | 'Backspace' | string

const MAC_GLYPH: Partial<Record<KeyToken, string>> = {
  mod: '⌘', Shift: '⇧', Alt: '⌥', Tab: '⇥', Enter: '⏎', Escape: '⎋', Backspace: '⌫',
}
const WIN_LABEL: Partial<Record<KeyToken, string>> = {
  mod: 'Ctrl', Shift: 'Shift', Alt: 'Alt', Tab: 'Tab', Enter: 'Enter', Escape: 'Esc', Backspace: 'Backspace',
}

/**
 * One combo, e.g. `['mod', 'Shift', 'F']` → `⌘⇧F` on a Mac, `Ctrl+Shift+F` on
 * Windows — the glyphs a Mac keyboard prints run together with no separator
 * (the OS's own convention, e.g. macOS menu bar shortcuts), Windows labels
 * join with `+` (that platform's own convention, e.g. Windows menu
 * accelerators).
 */
export function shortcutLabel(tokens: readonly KeyToken[]): string {
  const table = isMac ? MAC_GLYPH : WIN_LABEL
  const parts = tokens.map((t) => table[t] ?? t)
  return isMac ? parts.join('') : parts.join('+')
}
